import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import api from '../../../services/api';
import {
  getApprovalFlowUsers, assignUsers, removeUser, removeAllUsers,
  getConversionSuggestions, convertToDepartmentRule, previewImpact,
} from '../../../services/approvalFlowService';
import DataTable from '../../../components/ui/DataTable';
import PersonCell from '../../../components/ui/PersonCell';
import Button from '../../../components/ui/Button';
import AsyncGenericSelect from '../../../components/ui/AsyncGenericSelect';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { useImpactPreview } from '../../../components/ui/ImpactPreviewDialog';

// "Kişiye Özel" sekmesi (spec 2026-11-01 §4.5). Buradaki kişiler departman / ekip kurallarından bağımsız olarak bu
// akışla ilerler (kişiye özel atama her kuralı ezer). Departman / şube / grup seçimi BURADA YOK: onlar "Kurallar"
// sekmesinde canlı kuraldır — eskiden atama anında kişilere açılıyordu, sonradan gelen kişi akışı almıyordu.
// Bugünkü bağlamalar istisna olarak listelenir; tek tek ya da toplu kaldırılabilir (kaldırılan kişi kurallara düşer).
// Bir departmanın doğrudan çalışanlarının hepsi bu akışa bağlıysa "Departman Kuralına Çevir" önerilir.

const EmptyText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  text-align: center;
  padding: 24px;
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const DrawerTitle = styled.h3`
  font-size: 16px;
  color: #004242;
  font-weight: 700;
  font-family: 'Nunito Sans', sans-serif;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: none;
  color: #A0A8B4;
  cursor: pointer;
  &:hover { background: #F5F7FA; }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  /* Dropdown zIndex fix — her select'in dropdown'ı bir sonrakinin üstünde olmalı */
  & > div:nth-child(1) { z-index: 104; }
  & > div:nth-child(2) { z-index: 103; }
  & > div:nth-child(3) { z-index: 102; }
  & > div:nth-child(4) { z-index: 101; }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const Hint = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const Suggestion = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #F0F9FF;
  border: 1px solid #BAE6FD;
  color: #075985;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  margin-bottom: 8px;
`;

const DrawerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
`;

export default function FlowUsersTab({ flowId, flowType, refreshKey = 0, onChanged }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [confirmAll, setConfirmAll] = useState(false);
  const { runWithPreview, dialog: impactDialog } = useImpactPreview();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getApprovalFlowUsers(flowId);
      setUsers(res.data?.data || []);
      const sug = await getConversionSuggestions(flowId);
      setSuggestions(sug?.data?.status === true ? (sug.data.data || []) : []);
    } catch { /* interceptor */ }
    setLoading(false);
  }, [flowId]);

  useEffect(() => { fetchUsers(); }, [fetchUsers, refreshKey]);

  const afterChange = useCallback(async () => {
    await fetchUsers();
    if (onChanged) onChanged();
  }, [fetchUsers, onChanged]);

  // Search handlers
  const searchEmployees = useCallback(async (query) => {
    try {
      const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      return (res.data?.data?.data || []).map((e) => ({
        id: e.id,
        name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    } catch { return []; }
  }, []);

  const handleAddClick = useCallback(() => {
    setSelectedEmployees([]);
    setDrawerOpen(true);
  }, []);

  const handleAssignSave = useCallback(async () => {
    if (!selectedEmployees.length) return;
    setSaving(true);
    try {
      await assignUsers(flowId, { employeeIds: selectedEmployees });
      await afterChange();
      setDrawerOpen(false);
    } catch { /* interceptor */ }
    setSaving(false);
  }, [flowId, selectedEmployees, afterChange]);

  // Tek tek / seçili kaldırma: kaldırılan kişi kurallara düşer — akışı değişecekse önce liste gösterilir.
  const handleBulkDelete = useCallback(async (ids) => {
    const relationIds = users.filter((u) => ids.includes(u.userId || u.id)).map((u) => u.id);
    try {
      await runWithPreview(
        () => previewImpact({ flowType, removeRelationIds: relationIds }),
        async () => {
          for (const id of ids) {
            await removeUser(flowId, id);
          }
          await afterChange();
        },
      );
    } catch { /* interceptor */ }
  }, [flowId, flowType, users, runWithPreview, afterChange]);

  const handleRemoveAll = useCallback(async () => {
    setConfirmAll(false);
    try {
      await runWithPreview(
        () => previewImpact({ flowType, removeAllRelationsOfFlowId: flowId }),
        async () => {
          await removeAllUsers(flowId);
          await afterChange();
        },
      );
    } catch { /* interceptor */ }
  }, [flowId, flowType, runWithPreview, afterChange]);

  const handleConvert = useCallback(async (departmentId) => {
    try {
      await runWithPreview(
        () => previewImpact({ flowType, convertFlowId: flowId, convertDepartmentId: departmentId }),
        async () => {
          await convertToDepartmentRule(flowId, departmentId);
          await afterChange();
        },
      );
    } catch {
      // interceptor (ör. approvalFlow.conversionStale) — öneriler tazelenir
      await fetchUsers();
    }
  }, [flowId, flowType, runWithPreview, afterChange, fetchUsers]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('employee.fullName'),
      options: {
        filter: false, sort: false,
        customBodyRender: (val, tableMeta) => {
          const user = users[tableMeta.rowIndex];
          if (!user) return null;
          return <PersonCell name={user.userFullName || val || '-'} profilePicture={user.userProfilePicture} />;
        },
      },
    },
    { name: 'userEmail', label: t('login.email'), options: { filter: false, sort: false } },
  ], [t, users]);

  const tableData = useMemo(() =>
    users.map((u) => ({
      id: u.userId || u.id,
      userFullName: u.userFullName || '',
      userEmail: u.userEmail || '',
    })),
  [users]);

  return (
    <>
      <Toolbar>
        <Hint>{t('approvalFlow.exceptionsHint')}</Hint>
        <Actions>
          {users.length > 0 && (
            <Button variant="outline" onClick={() => setConfirmAll(true)} data-testid="flow-exceptions-remove-all">
              {t('approvalFlow.removeAllExceptions')}
            </Button>
          )}
          <Button onClick={handleAddClick}>+ {t('approvalFlow.assignUsers')}</Button>
        </Actions>
      </Toolbar>

      {suggestions.map((sg) => (
        <Suggestion key={sg.departmentId} role="status" data-testid="flow-conversion-suggestion">
          <span>{t('approvalFlow.conversionSuggestion', { department: sg.departmentPath, count: sg.memberCount })}</span>
          <Button variant="outline" onClick={() => handleConvert(sg.departmentId)} data-testid="flow-convert">
            {t('approvalFlow.convertToDepartmentRule')}
          </Button>
        </Suggestion>
      ))}

      {loading ? (
        <EmptyText>{t('common.loading')}</EmptyText>
      ) : (
        <DataTable
          title=""
          columns={columns}
          data={tableData}
          totalRowCount={tableData.length}
          pageIndex={0}
          pageSize={tableData.length || 10}
          onRowsDelete={handleBulkDelete}
          customIdSelector="id"
        />
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100vw',
          minWidth: 380,
          margin: '12px',
          borderRadius: '6px',
          height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
          zIndex: 1000,
        }}
      >
        <DrawerHeader>
          <DrawerTitle>{t('approvalFlow.assignUsers')}</DrawerTitle>
          <CloseBtn onClick={() => setDrawerOpen(false)}><X size={20} /></CloseBtn>
        </DrawerHeader>

        <DrawerBody>
          <AsyncGenericSelect
            label={t('approvalFlow.assignByEmployee')}
            value={selectedEmployees}
            onChange={setSelectedEmployees}
            onSearch={searchEmployees}
            renderOption={(opt) => <PersonCell name={opt.name} profilePicture={opt.profilePicture} />}
            isMulti
          />

        </DrawerBody>

        <DrawerFooter>
          <Button variant="outline" onClick={() => setDrawerOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleAssignSave} disabled={saving}>
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </DrawerFooter>
      </Drawer>

      <ConfirmDialog
        isOpen={confirmAll}
        title={t('approvalFlow.removeAllExceptions')}
        message={t('approvalFlow.removeAllExceptionsConfirm', { count: users.length })}
        onConfirm={handleRemoveAll}
        onCancel={() => setConfirmAll(false)}
      />

      {impactDialog}
    </>
  );
}
